import { Icon } from '@iconify/react'
import { Box, Typography, useTheme } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { memo, useMemo, useRef, useState } from 'react'
import type { Kanban } from './types'

const getKanbanStatusConfig = (scanned: boolean, hasQuantityMismatch: boolean, theme: Theme) => {
  const variant = scanned ? (hasQuantityMismatch ? 'warning' : 'success') : 'danger'
  const label = scanned ? 'Scanned' : 'Unscanned'
  return {
    label,
    color: (theme as any).colorToken?.text?.[variant]?.normal || theme.palette.error.main,
    backgroundColor: (theme as any).colorToken?.background?.[variant]?.subtlest || theme.palette.error.light
  }
}

const KanbanStatus = memo<{ scanned: boolean; hasQuantityMismatch?: boolean }>(({ scanned, hasQuantityMismatch }) => {
  const theme = useTheme()
  const statusConfig = useMemo(
    () => getKanbanStatusConfig(scanned, !!hasQuantityMismatch, theme),
    [scanned, hasQuantityMismatch, theme]
  )

  return (
    <Box
      sx={{
        height: 16,
        px: 1,
        borderRadius: 1,
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 12,
        backgroundColor: statusConfig.backgroundColor,
        color: statusConfig.color
      }}
    >
      {statusConfig.label}
    </Box>
  )
})

interface KanbanItemProps extends Kanban {
  onQuantityAdjust?: (kanban: Kanban) => void
  onScanKanban?: (kanban: Kanban) => void
  actionPosition?: 'left' | 'right'
  disableSwipe?: boolean
  viewOnly?: boolean
  hideCode?: boolean
  rightContent?: React.ReactNode
}

const KanbanItem = memo<KanbanItemProps>(
  ({
    name,
    rack,
    scanned,
    quantity,
    expectedQuantity,
    inputtedQuantity,
    hasQuantityMismatch,
    qrCode,
    binningQty,
    onQuantityAdjust,
    onScanKanban,
    actionPosition = 'right',
    disableSwipe = false,
    viewOnly = false,
    hideCode = false,
    rightContent
  }) => {
    const theme = useTheme()
    const kanbanData = {
      name,
      rack,
      scanned,
      quantity,
      expectedQuantity,
      inputtedQuantity,
      hasQuantityMismatch,
      qrCode,
      binningQty
    }

    const handleQuantityClick = () => scanned && onQuantityAdjust?.(kanbanData)
    const handleScanClick = () => !scanned && onScanKanban?.(kanbanData)

    const quantityVariant = hasQuantityMismatch ? 'warning' : 'primary'
    const shownQuantity =
      typeof binningQty === 'number' && binningQty > 0
        ? binningQty
        : viewOnly
          ? quantity
          : disableSwipe
            ? (inputtedQuantity ?? quantity)
            : quantity
    const hasBinningQty = typeof binningQty === 'number' && binningQty > 0

    const REVEAL_WIDTH = 42
    const SIDEBAR_MIN = 18
    const THRESHOLD = 24
    const [translateX, setTranslateX] = useState(0)
    const [dragging, setDragging] = useState(false)
    const startXRef = useRef(0)

    const onTouchStart = (e: React.TouchEvent) => {
      if (disableSwipe || scanned || viewOnly) return
      startXRef.current = e.touches[0].clientX
      setDragging(true)
    }
    const onTouchMove = (e: React.TouchEvent) => {
      if (disableSwipe || scanned || viewOnly || !dragging) return
      const currentX = e.touches[0].clientX
      const delta = startXRef.current - currentX
      if (delta <= 0) {
        setTranslateX(0)
      } else {
        setTranslateX(Math.min(REVEAL_WIDTH, delta))
      }
    }
    const onTouchEnd = () => {
      if (scanned || viewOnly) return
      setDragging(false)
      const final = translateX > THRESHOLD ? REVEAL_WIDTH : 0
      setTranslateX(final)
      if (final === REVEAL_WIDTH) {
        onScanKanban?.(kanbanData)
        setTranslateX(0)
        setDragging(false)
      }
    }

    const getBackgroundColor = () => {
      if (scanned && hasQuantityMismatch) {
        return theme.colorToken.background.warning.normal
      }
      if (scanned) {
        return theme.colorToken.background.success.normal
      }
      return theme.colorToken.background.danger.normal
    }

    const panelWidth = Math.max(SIDEBAR_MIN, Math.min(REVEAL_WIDTH, translateX))
    const ActionPanel = (
      <Box
        onClick={!scanned ? handleScanClick : handleQuantityClick}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: panelWidth,
          background: getBackgroundColor(),
          color: theme.colorToken.text.neutralInverted.normal,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,
          userSelect: 'none',
          zIndex: 0
        }}
      >
        {panelWidth <= SIDEBAR_MIN + 6 ? (
          <Icon
            icon={scanned || hasQuantityMismatch ? 'tabler:checks' : 'tabler:chevrons-left'}
            color='white'
            fontSize={12}
          />
        ) : (
          <>
            <Icon icon={!scanned ? 'tabler:scan' : 'tabler:box'} color='white' fontSize={14} />
            <Typography color='white' variant='caption'>
              Box
            </Typography>
          </>
        )}
      </Box>
    )

    return (
      <Box
        sx={{
          position: 'relative',
          background: theme.colorToken?.background?.neutral?.subtlest || 'grey.50',
          borderRadius: 2,
          border: `1px solid ${theme.palette.neutral?.[50] || '#e0e0e0'}`,
          mb: 2,
          overflow: 'hidden',
          px: 1,
          py: 2
        }}
      >
        {!disableSwipe && !viewOnly && ActionPanel}
        <Box
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pl: 1.5,
            pr: disableSwipe || viewOnly ? 1.5 : 7,
            py: 1.5,
            width: '100%',
            position: 'relative',
            zIndex: 1,
            touchAction: 'pan-y'
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant='body2' fontWeight={500}>
                {name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary' }}>
                {!hideCode && <Typography variant='caption'>{rack}</Typography>}
                {!disableSwipe && !viewOnly && (
                  <KanbanStatus scanned={scanned} hasQuantityMismatch={hasQuantityMismatch} />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                transform: disableSwipe || viewOnly ? 'none' : `translateX(-${translateX - 17}px)`,
                transition: dragging ? 'none' : 'transform 220ms ease-out',
                touchAction: 'pan-y'
              }}
            >
              <Box>
                {rightContent ? (
                  rightContent
                ) : (
                  <Box
                    onClick={scanned ? handleQuantityClick : undefined}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: hasBinningQty
                        ? theme.colorToken.background.neutral.subtle
                        : scanned
                          ? theme.colorToken.background[quantityVariant].subtlest
                          : theme.colorToken.background.primary.subtlest,
                      color: hasBinningQty
                        ? theme.colorToken.text.neutral.subtle
                        : scanned
                          ? theme.colorToken.text[quantityVariant].normal
                          : theme.colorToken.text.primary.normal,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: scanned ? 'pointer' : 'default'
                    }}
                  >
                    {shownQuantity}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }
)

export default KanbanItem
